import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Form, Badge, Spinner } from "react-bootstrap";
import api from "../../api";
import ProjectMemberPicker from "../../components/ProjectMemberPicker";
import { usePermissions } from "../../context/PermissionContext";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";

const MEMBERSHIP_TYPES = ["OWNER", "MANAGER", "MEMBER", "REVIEWER", "TESTER", "OBSERVER"];

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// Phase 1B UI extension. A dedicated "Module Members" tab on
// ProjectDetailPage: pick ANY module for the project first, then manage
// that module's project_module_members with the fuller column set the
// feature spec calls for (email, role via the project-member enrichment,
// module-manager flag, joined date, edit + soft remove). This is separate
// from ProjectModuleManagement's own per-row "Members" quick-modal (left
// unchanged) but calls the exact same
// /projects/:id/modules/:moduleId/members endpoints -- no new backend
// surface was added for this tab.
export default function ModuleMembersManagement({ projectId, projectMembers = [] }) {
  const { can } = usePermissions();

  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState("");

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [newMemberUserId, setNewMemberUserId] = useState("");
  const [newMemberType, setNewMemberType] = useState("MEMBER");
  const [newMemberIsManager, setNewMemberIsManager] = useState(false);

  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (projectId) fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (selectedModuleId) fetchMembers(selectedModuleId);
    else setMembers([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuleId]);

  const fetchModules = async () => {
    try {
      setLoadingModules(true);
      const res = await api.get(`/projects/${projectId}/modules`, { params: { pageSize: 200 } });
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setModules(rows);
      setSelectedModuleId((prev) => prev || (rows[0] ? String(rows[0].id) : ""));
    } catch (err) {
      console.error("Fetch modules error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch project modules");
    } finally {
      setLoadingModules(false);
    }
  };

  const fetchMembers = async (moduleId) => {
    try {
      setLoadingMembers(true);
      const res = await api.get(`/projects/${projectId}/modules/${moduleId}/members`, { params: { pageSize: 200 } });
      setMembers(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Fetch module members error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch module members");
    } finally {
      setLoadingMembers(false);
    }
  };

  // project_module_members' SELECT does not join email/role_name (see
  // services/projectModuleMemberService.js) -- but every module member is
  // required (backend-enforced) to already be an active project member,
  // and ProjectDetailPage already enriches `projectMembers` with
  // email/role_name from GET /users, so cross-referencing here needs no
  // extra fetch or backend change.
  const projectMembersByUserId = useMemo(() => {
    const map = new Map();
    projectMembers.forEach((m) => map.set(Number(m.user_id), m));
    return map;
  }, [projectMembers]);

  const membersWithUserInfo = useMemo(
    () => members.map((m) => {
      const pm = projectMembersByUserId.get(Number(m.user_id));
      return { ...m, email: pm?.email, role_name: pm?.role_name };
    }),
    [members, projectMembersByUserId]
  );

  const activeModuleMemberUserIds = useMemo(
    () => membersWithUserInfo.filter((m) => m.is_active).map((m) => m.user_id),
    [membersWithUserInfo]
  );

  const selectedModule = modules.find((m) => String(m.id) === String(selectedModuleId));
  const canManage = can("project_module_members", "add") || can("project_module_members", "manage");
  const canEdit = can("project_module_members", "edit") || can("project_module_members", "manage");
  const canRemove = can("project_module_members", "remove") || can("project_module_members", "manage");

  const addMember = async () => {
    if (!selectedModuleId || !newMemberUserId) return;
    try {
      await api.post(`/projects/${projectId}/modules/${selectedModuleId}/members`, {
        user_id: newMemberUserId,
        membership_type: newMemberType,
        is_module_manager: newMemberIsManager,
      });
      toast.success("Module member assigned");
      setNewMemberUserId("");
      setNewMemberType("MEMBER");
      setNewMemberIsManager(false);
      fetchMembers(selectedModuleId);
    } catch (err) {
      console.error("Assign module member error:", err);
      const data = err.response?.data;
      if (data?.code === "MODULE_MEMBER_EXISTS") {
        toast.error("This user is already an active member of this module.");
      } else if (data?.code === "MODULE_MEMBER_NOT_PROJECT_MEMBER") {
        toast.error("This user must first be added as a project member.");
      } else {
        toast.error(data?.message || "Failed to assign module member");
      }
    }
  };

  const updateMembershipType = async (membership, membershipType) => {
    try {
      await api.put(`/projects/${projectId}/modules/${selectedModuleId}/members/${membership.id}`, { membership_type: membershipType });
      toast.success("Membership updated");
      fetchMembers(selectedModuleId);
    } catch (err) {
      console.error("Update module membership error:", err);
      toast.error(err.response?.data?.message || "Failed to update membership");
    }
  };

  const toggleManagerFlag = async (membership) => {
    try {
      await api.put(`/projects/${projectId}/modules/${selectedModuleId}/members/${membership.id}`, { is_module_manager: !membership.is_module_manager });
      fetchMembers(selectedModuleId);
    } catch (err) {
      console.error("Update module manager flag error:", err);
      toast.error(err.response?.data?.message || "Failed to update membership");
    }
  };

  const toggleActive = async (membership) => {
    try {
      await api.patch(`/projects/${projectId}/modules/${selectedModuleId}/members/${membership.id}/status`, { is_active: !membership.is_active });
      toast.success(membership.is_active ? "Membership deactivated" : "Membership reactivated");
      fetchMembers(selectedModuleId);
    } catch (err) {
      console.error("Toggle module membership error:", err);
      toast.error(err.response?.data?.message || "Failed to update membership");
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await api.delete(`/projects/${projectId}/modules/${selectedModuleId}/members/${removeTarget.id}`);
      toast.success("Module member removed");
      setRemoveTarget(null);
      fetchMembers(selectedModuleId);
    } catch (err) {
      console.error("Remove module member error:", err);
      toast.error(err.response?.data?.message || "Failed to remove module member");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div style={{ minWidth: 260 }}>
          <Form.Label className="small mb-1">Project Module</Form.Label>
          <Form.Select
            value={selectedModuleId}
            onChange={(e) => setSelectedModuleId(e.target.value)}
            disabled={loadingModules || modules.length === 0}
          >
            {modules.length === 0 && <option value="">No modules yet</option>}
            {modules.map((m) => (
              <option key={m.id} value={m.id}>{m.name}{m.code ? ` (${m.code})` : ""}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      {!selectedModuleId ? (
        <div className="unit-empty">
          <div className="unit-empty-icon"><i className="fa-solid fa-layer-group" aria-hidden="true"></i></div>
          <h5>No modules configured for this project.</h5>
          <p>Create a module in the Modules tab first.</p>
        </div>
      ) : (
        <>
          {canManage && (
            <>
              <div className="d-flex gap-2 mb-2 flex-wrap">
                <div style={{ flex: 1, minWidth: 260 }}>
                  <ProjectMemberPicker
                    users={projectMembers.map((pm) => ({
                      id: pm.user_id,
                      name: pm.user_name,
                      employee_code: pm.employee_code,
                      designation_name: pm.designation_name,
                      department_name: pm.department_name,
                    }))}
                    excludeUserIds={activeModuleMemberUserIds}
                    value={newMemberUserId}
                    onChange={setNewMemberUserId}
                    placeholder="Search active project members to assign..."
                  />
                </div>
                <Form.Select style={{ maxWidth: 160 }} value={newMemberType} onChange={(e) => setNewMemberType(e.target.value)}>
                  {MEMBERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
                <Button className="unit-btn unit-btn-primary" onClick={addMember} disabled={!newMemberUserId}>
                  <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
                  Assign Member
                </Button>
              </div>
              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Is Module Manager"
                  checked={newMemberIsManager}
                  onChange={(e) => setNewMemberIsManager(e.target.checked)}
                />
              </div>
            </>
          )}

          {projectMembers.length === 0 && (
            <p className="text-danger small">Add project members first (Members tab) before assigning module members.</p>
          )}

          {loadingMembers ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Table responsive className="unit-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Membership Type</th>
                  <th>Module Manager</th>
                  <th>Joined At</th>
                  <th>Active</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {membersWithUserInfo.map((m) => (
                  <tr key={m.id}>
                    <td>{m.user_name}{m.employee_code ? <span className="text-muted"> ({m.employee_code})</span> : null}</td>
                    <td>{m.email || "—"}</td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={m.membership_type}
                        style={{ minWidth: 120 }}
                        disabled={!canEdit}
                        onChange={(e) => updateMembershipType(m, e.target.value)}
                      >
                        {MEMBERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Form.Select>
                    </td>
                    <td>
                      <Badge
                        bg={m.is_module_manager ? "info" : "light"}
                        text={m.is_module_manager ? undefined : "dark"}
                        style={canEdit ? { cursor: "pointer" } : undefined}
                        onClick={canEdit ? () => toggleManagerFlag(m) : undefined}
                        title={canEdit ? "Click to toggle" : undefined}
                      >
                        {m.is_module_manager ? "Yes" : "No"}
                      </Badge>
                    </td>
                    <td>{formatDateTime(m.joined_at)}</td>
                    <td><Badge bg={m.is_active ? "success" : "secondary"}>{m.is_active ? "Active" : "Inactive"}</Badge></td>
                    <td>
                      <div className="unit-actions">
                        {canEdit && (
                          <Button
                            className="unit-icon-btn projdetail-toggle"
                            title={m.is_active ? "Deactivate" : "Reactivate"}
                            aria-label={m.is_active ? `Deactivate ${m.user_name || "member"}` : `Reactivate ${m.user_name || "member"}`}
                            onClick={() => toggleActive(m)}
                          >
                            <i className={`fa-solid ${m.is_active ? "fa-pause" : "fa-play"}`} aria-hidden="true"></i>
                          </Button>
                        )}
                        {canRemove && (
                          <Button
                            className="unit-icon-btn unit-delete"
                            title="Remove"
                            aria-label={`Remove ${m.user_name || "member"}`}
                            onClick={() => setRemoveTarget(m)}
                          >
                            <i className="fa-solid fa-user-minus" aria-hidden="true"></i>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {membersWithUserInfo.length === 0 && (
                  <tr>
                    <td colSpan="7">
                      <div className="unit-empty">
                        <div className="unit-empty-icon"><i className="fa-solid fa-users" aria-hidden="true"></i></div>
                        <h5>No members assigned to this module.</h5>
                        <p>Assign the first member above.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </>
      )}

      <ConfirmModal
        show={!!removeTarget}
        onHide={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        busy={removing}
        title="Remove Module Member?"
        message={`Remove ${removeTarget?.user_name || "this member"} from "${selectedModule?.name || "this module"}"?`}
        confirmLabel="Remove"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
      />
    </div>
  );
}
